import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { salesService } from '../services/salesService';
import { SaleFormData } from '../types/Sales';

export const useSales = () => {
  return useQuery({
    queryKey: ['sales'],
    queryFn: salesService.getAll,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useSale = (id: number) => {
  return useQuery({
    queryKey: ['sales', id],
    queryFn: () => salesService.getById(id),
    enabled: !!id,
  });
};

export const useSalesHistory = (params: {
  startDate: string;
  endDate: string;
  barcode?: string;
}) => {
  return useQuery({
    queryKey: ['sales', 'history', params],
    queryFn: () => salesService.getHistory(params),
    enabled: !!params.startDate && !!params.endDate, // Only run query when dates are provided
  });
};

export const useCreateSale = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SaleFormData) => salesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
};

export const useUpdateSale = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: SaleFormData }) =>
      salesService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
};

export const useDeleteSale = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => salesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
};

